import { Button, Tab, Tabs } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { createCode, getCodes, saveDraftCode, updateCode } from 'api/accessCode'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getTitle } from 'components/tabs/TabsTitle'
import { EAccessCodeStatus } from 'features/AccessManagement/hooks/useOrganizationFormData'
import { getActionColumn } from 'features/UI'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useShowControl } from 'hooks/useShowControl'
import { useTabsControl } from 'hooks/useTabsControl'
import { useCallback, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { AccessCode, BaseTableRequest, EAccessCodeType, EOp, Nullish } from 'types'
import { useGetDataSource } from 'utils'
import { useAccessCodeRowStyle } from '../../hooks/getAccessCodeRowStyle'
import { AccessCodeGenerationModal } from '../AccessCodeGenerationModal/AccessCodeGenerationModal'
import {
  csvInstitutionalCodeColumns,
  csvPromoCodeColumns,
  institutionalCodeColumns,
  promoCodeColumns,
} from './csvColumns'

export const AccessManagement = () => {
  const { selectedTab, handleChangeSelectedTab } = useTabsControl<EAccessCodeType>(
    EAccessCodeType.institutionAccessCode,
  )

  const { isAdmin } = useAuthContext()

  const [isOpenGenerate, handleOpenGenerate, handleCloseGenerate] = useShowControl()
  const [isEdit, handleOpenEdit, handleCloseEdit] = useShowControl()
  const [editedItem, setEditedItem] = useState<Nullish<AccessCode>>(null)

  const openGenerateNew = useCallback(() => {
    handleOpenGenerate()
    setEditedItem(null)
  }, [handleOpenGenerate])

  const handleClickEdit = useCallback(
    (_: number, item: ICellRendererParams<AccessCode>) => {
      if (!item.data) return

      if (item.data.status === EAccessCodeStatus.draft) {
        handleOpenGenerate()
        setEditedItem(item.data)
      } else {
        setEditedItem(item.data)
        handleOpenEdit()
      }
    },
    [handleOpenEdit, handleOpenGenerate],
  )

  const columns = useMemo(() => {
    if (selectedTab === EAccessCodeType.institutionAccessCode) {
      let columns = [...institutionalCodeColumns]
      if (isAdmin) {
        columns = columns.concat(
          getActionColumn([
            {
              name: 'Edit',
              onClick: handleClickEdit,
            },
          ]),
        )
      }
      return columns
    }

    if (selectedTab === EAccessCodeType.promoCode) {
      return promoCodeColumns
    }

    return []
  }, [handleClickEdit, isAdmin, selectedTab])

  const getCodeWrapper = useCallback(
    (request: BaseTableRequest) => {
      if (request.filter && request.filter?.organizationName) {
        request.filter['or'] = [
          { ['organizationName']: request.filter.organizationName },
          { ['organization.name']: request.filter.organizationName },
        ]

        delete request.filter.organizationName
      }

      return getCodes({
        ...request,
        filter: {
          ...request.filter,
          codeType: {
            type: 'text',
            op: EOp.equals,
            value: selectedTab,
          },
        },
      })
    },
    [selectedTab],
  )

  const getDataSource = useGetDataSource(getCodeWrapper)
  const { gridApi, onGridReady, columnApi } = useGridControl(getDataSource)

  const mutation = useMutation(createCode, {
    onSuccess: (data) => {
      handleCloseGenerate()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Created access code ${data.data.code}`)
    },
  })

  const saveDraftMutation = useMutation(saveDraftCode, {
    onSuccess: (data) => {
      handleCloseGenerate()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Draft access code ${data.data.code} saved`)
    },
  })

  const onCloseEdit = useCallback(() => {
    setEditedItem(null)
    handleCloseEdit()
  }, [handleCloseEdit])

  const updateMutation = useMutation(updateCode, {
    onSuccess: (data) => {
      onCloseEdit()
      gridApi?.purgeInfiniteCache()
      NotificationSys.showSuccess(`Updated access code ${data.data.code}`)
    },
  })

  const { csvLoading, onExportCSV } = useExportCSV(
    getCodeWrapper,
    gridApi,
    columnApi,
    selectedTab === EAccessCodeType.institutionAccessCode
      ? csvInstitutionalCodeColumns
      : csvPromoCodeColumns,
    `${selectedTab} Access Codes.csv`,
  )

  const getRowStyle = useAccessCodeRowStyle()

  const left = (
    <>
      <Tabs value={selectedTab} onChange={handleChangeSelectedTab} indicatorColor="primary">
        <Tab
          value={EAccessCodeType.institutionAccessCode}
          label={getTitle('Institutional Access Codes')}
        />
        <Tab value={EAccessCodeType.promoCode} label={getTitle('Promo Codes')} />
      </Tabs>
    </>
  )

  return (
    <>
      <TableToolbar
        exportCSVEnable={true}
        left={left}
        onExportCSV={onExportCSV}
        csvLoading={csvLoading}
      >
        {isAdmin ? (
          <Button variant="outlined" sx={{ minWidth: 120 }} onClick={openGenerateNew}>
            Generate Code
          </Button>
        ) : null}
      </TableToolbar>

      <InfinityAgGrid
        pagination
        columnDefs={columns}
        getRowStyle={getRowStyle}
        onGridReady={onGridReady}
      />

      <AccessCodeGenerationModal
        onSaveDraft={saveDraftMutation.mutate}
        loading={mutation.isLoading}
        isOpen={isOpenGenerate}
        handleClose={handleCloseGenerate}
        onGenerate={mutation.mutate}
        onUpdate={updateMutation.mutate}
        type={selectedTab}
        editedItem={editedItem}
      />

      <AccessCodeGenerationModal
        onSaveDraft={saveDraftMutation.mutate}
        loading={updateMutation.isLoading}
        isOpen={isEdit}
        handleClose={onCloseEdit}
        onGenerate={mutation.mutate}
        onUpdate={updateMutation.mutate}
        type={selectedTab}
        editedItem={editedItem}
      />
    </>
  )
}
